import React, { useEffect } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { useFormik } from 'formik';
import MenuItem from '@material-ui/core/MenuItem';
import * as Yup from 'yup';

import defaultValidationSchema from './ModifyRuleSensorModal.schema';
import EquipmentSourceFieldset from './EquipmentSourceFieldset';
import GeneralSourceFieldset from './GeneralSourceFieldset';
import SystemSourceFieldset from './SystemSourceFieldset';
import WeatherSourceFieldset from './WeatherSourceFieldset';
import EsmsDialog from '../../../components/EsmsDialog';
import Select from '../../../components/FormFields/Select';
import SensorAggregationFields from './SensorAggregationFields';
import useFormId from '../../../hooks/useFormId';
import usePrevious from '../../../hooks/usePrevious';

import defaultClasses from './ModifyRuleSensorModal.module.css';

const TITLES = {
    [false]: 'Modify Sensor Filter',
    [true]:  'Add Sensor Filter'
};
const CONFIRM_LABELS = {
    [false]: 'Update Filter',
    [true]:  'Add Filter'
};
const SOURCE_TYPES = {
    General:   'General Sensor',
    System:    'System Sensor',
    Equipment: 'Equipment Sensor',
    Weather:   'Weather Sensor'
};

const ModifyRuleSensorModal = ({
    defaultSensor,
    onClose,
    onConfirm,
    open,
    ruleTarget,
    sensor
}) => {
    /**
     * The `sensorSourcing` validation requirements depend on an outside value,
     * specifically the current value of the `target` value of the rule that
     * this sensor is to be added to. This pulls the `sensorSourcing` validation
     * config and "injects" the rule target value into it using Yup's `meta`
     * function. Since `reach` returns an immutable object, the rule is then
     * merged back into the validation schema via Yup's `concat` function.
     */
    const sensorSourcing = Yup.reach(defaultValidationSchema, 'sensorSourcing');
    const validationSchema = defaultValidationSchema.concat(
        Yup.object().shape({
            sensorSourcing: sensorSourcing.meta({ ruleTarget })
        })
    );

    const {
        errors,
        handleBlur,
        handleChange,
        handleReset,
        handleSubmit: formikHandleSubmit,
        isSubmitting,
        resetForm,
        setFieldValue,
        touched,
        values
    } = useFormik({
        initialValues:    defaultSensor,
        onSubmit:         handleSubmit,
        validationSchema,
        validateOnChange: false
    });
    const formId = useFormId('sensor');
    const prevSensorAggregateType = usePrevious(values.sensorAggregateType);
    const prevSourceType = usePrevious(values.sourceType);
    const prevOpen = usePrevious(open);

    /**
     * Reset the form when the modal closes
     */
    useEffect(() => {
        if (prevOpen !== open && !open) {
            resetForm();
        }
    }, [open]);

    /**
     * Reset the form if the sensor prop changes
     */
    useEffect(() => {
        resetForm({ values: sensor });
    }, [sensor]);

    /**
     * Reset all Sensor values when the Source Type value changes.
     *
     * This form contains fields that are only rendered when sensor values are
     * set. When those criteria change, the form is reset to prevent field
     * values that are no longer applicable from being persisted to the sensor.
     */
    useEffect(() => {
        if (prevSourceType && prevSourceType !== values.sourceType) {
            const resetValues = {
                ...defaultSensor,
                id:         values.id,
                sourceType: values.sourceType
            };
            resetForm({ values: resetValues });
        }
    }, [resetForm, values]);

    /**
     * Reset all Sensor aggregate values when the Aggregate Type value is set..
     *
     * If the aggregate value changes from a known value to "None", then all
     * aggregate-related values are reset to prevent aggregate  values that are
     * no longer applicable from being persisted to the sensor.
     */
    useEffect(() => {
        if (
            prevSensorAggregateType !== values.sensorAggregateType
            &&
            values.sensorAggregateType === 'None'
        ) {
            const resetValues = {
                ...values,
                sensorAggregateDuration: 0
            };
            resetForm({ values: resetValues });
        }
    }, [resetForm, values]);

    /**
     * There are certain fields that need to be able to set multiple values in
     * one form interaction. Because React re-uses a single `SyntheticEvent`,
     * it is not possible to manually trigger two `onChange` events. Instead,
     * this form introduces a pseudo "onPatch" handler that provides the means
     * to change multiple values in one go. In order to update Formik with the
     * new group of values, this handler uses the stored reference to Formik's
     * `setFieldValue` function and manually calls it for each key of the
     * provided changes, thus accomplishing the goal of changing multiple values
     * in one interaction.
     */
    function handlePatch(patch) {
        Object.entries(patch).forEach(([fieldName, value]) => {
            setFieldValue(fieldName, value, false);
        });
    }

    /**
     * Handle the Formik submit event
     */
    function handleSubmit(values, actions) {
        onConfirm(values);
        onClose();
    }

    const isNewSensor = Boolean(!values.id);
    const isEquipmentSensor = values.sourceType === 'Equipment';
    const isGeneralSensor = values.sourceType === 'General';
    const isSystemSensor = values.sourceType === 'System';
    const isWeatherSensor = values.sourceType === 'Weather';
    return (
        <EsmsDialog
            confirmFormId={formId}
            confirmText={CONFIRM_LABELS[isNewSensor]}
            confirmType="submit"
            isLoading={isSubmitting}
            onClose={onClose}
            open={open}
            title={TITLES[isNewSensor]}
        >
            <form
                className={defaultClasses.form}
                id={formId}
                onReset={handleReset}
                onSubmit={formikHandleSubmit}
            >
                <Select
                    errors={errors.sourceType}
                    label="Sensor Source Type"
                    name="sourceType"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    touched={touched.sourceType}
                    value={values.sourceType}
                >
                    {Object.entries(SOURCE_TYPES).map(([value, label]) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </Select>
                <EquipmentSourceFieldset
                    errors={errors}
                    hidden={!isEquipmentSensor}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onPatch={handlePatch}
                    ruleTarget={ruleTarget}
                    touched={touched}
                    values={values}
                />
                <GeneralSourceFieldset
                    errors={errors}
                    hidden={!isGeneralSensor}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onPatch={handlePatch}
                    ruleTarget={ruleTarget}
                    touched={touched}
                    values={values}
                />
                <SystemSourceFieldset
                    errors={errors}
                    hidden={!isSystemSensor}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onPatch={handlePatch}
                    ruleTarget={ruleTarget}
                    touched={touched}
                    values={values}
                />
                <WeatherSourceFieldset
                    errors={errors}
                    hidden={!isWeatherSensor}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onPatch={handlePatch}
                    touched={touched}
                    values={values}
                />
                <SensorAggregationFields
                    errors={errors}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    touched={touched}
                    values={values}
                />
            </form>
        </EsmsDialog>
    );
};

ModifyRuleSensorModal.defaultProps = {
    defaultSensor: {},
    open:          false,
    sensor:        {}
};

ModifyRuleSensorModal.propTypes = {
    defaultSensor: object,
    onClose:       func,
    open:          bool,
    ruleTarget:    string,
    sensor:        shape({
        sourceType: string
    })
};

export default ModifyRuleSensorModal;
